import { useEffect, useRef, useState } from 'react';
import { apiService, MeetingProgress } from '../services/api.service';

/** Задача L1: интервал поллинга живого прогресса — эндпоинт дешёвый (только чтение Meeting). */
const PROGRESS_POLL_MS = 4000;

/** Список записей на странице «Файлы» может показывать прогресс не более, чем для
 *  этого числа одновременно обрабатываемых встреч — чтобы не разгонять поллинг
 *  при большом бэклоге в очереди. */
const MAX_CONCURRENT_LIST_POLLS = 5;

function isPageVisible(): boolean {
  if (typeof document === 'undefined') return true;
  return document.visibilityState !== 'hidden';
}

async function fetchProgressSafe(meetingId: string): Promise<MeetingProgress | null> {
  try {
    return await apiService.getMeetingProgress(meetingId);
  } catch {
    // transient network errors — оставляем предыдущее значение, попробуем на следующем тике
    return null;
  }
}

/**
 * Живой прогресс обработки одной встречи: поллинг GET /progress каждые 4с, пока
 * `enabled` и статус — "processing". Останавливается на unmount и как только статус
 * перестаёт быть "processing" (после этого разово вызывает onComplete, если задан).
 */
export function useMeetingProgress(
  meetingId: string | undefined,
  enabled: boolean,
  onComplete?: (data: MeetingProgress) => void,
): { progress: MeetingProgress | null } {
  const [progress, setProgress] = useState<MeetingProgress | null>(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  useEffect(() => {
    if (!meetingId || !enabled) {
      setProgress(null);
      completedRef.current = false;
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      if (!isPageVisible()) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
        return;
      }

      const data = await fetchProgressSafe(meetingId);
      if (cancelled || !data) {
        if (!cancelled) timer = setTimeout(tick, PROGRESS_POLL_MS);
        return;
      }

      setProgress(data);

      if (data.status !== 'processing') {
        if (!completedRef.current) {
          completedRef.current = true;
          onCompleteRef.current?.(data);
        }
        return; // остановка поллинга — обработка завершена (или упала)
      }

      if (!cancelled) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [meetingId, enabled]);

  return { progress };
}

/**
 * Живой прогресс для списка встреч (страница «Файлы»): один общий поллинг вместо
 * поштучного — берёт первые MAX_CONCURRENT_LIST_POLLS обрабатываемых id и опрашивает
 * их пачкой раз в 4с. Возвращает карту id -> MeetingProgress.
 */
export function useMeetingsProgress(
  processingIds: string[],
  enabled: boolean,
): Record<string, MeetingProgress> {
  const [progressById, setProgressById] = useState<Record<string, MeetingProgress>>({});
  // Стабильный ключ для эффекта — не зависеть от нового массива на каждый рендер родителя.
  const idsKey = processingIds.slice(0, MAX_CONCURRENT_LIST_POLLS).join(',');

  useEffect(() => {
    const ids = idsKey ? idsKey.split(',') : [];
    if (!enabled || ids.length === 0) {
      setProgressById({});
      return;
    }

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;

    const tick = async () => {
      if (cancelled) return;
      if (!isPageVisible()) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
        return;
      }

      const results = await Promise.all(ids.map(id => fetchProgressSafe(id)));
      if (cancelled) return;

      setProgressById(prev => {
        const next = { ...prev };
        results.forEach((data, idx) => {
          if (data) next[ids[idx]] = data;
        });
        return next;
      });

      if (!cancelled) {
        timer = setTimeout(tick, PROGRESS_POLL_MS);
      }
    };

    void tick();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [idsKey, enabled]);

  return progressById;
}
